import { sameMoneyAmount } from './timedFeeDefaults';
import { TimedFeeEditor } from './TimedFeeEditor';
import type { TimedFeeTable } from '@pokerroom/types';
import { brand } from '../../lib/brand';
/**
 * @page TournamentTemplatesPage
 * @description 賽事模板管理（CRUD）
 * Route: /tournament/templates
 */

import { BrandIcon } from '../../components/layout/BrandIcon';
import { useSettlementLevels } from './TimedSettlementLevelsPanel';
import { useEffect, useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, type FieldErrors, type UseFormRegister } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IconChevronDown } from '@tabler/icons-react';
import { isAxiosError } from 'axios';
import type {
  TournamentTemplateDto,
  CreateTemplateDto,
  BlindStructureDto,
  CatalogImportPreview,
  CatalogImportResult,
  CatalogPackage,
  PayoutRule,
  TournamentType,
  CatalogResolution,
} from '@pokerroom/types';
import { api } from '../../lib/api.ts';
import {
  buildDeadlineOptions,
  clampDeadlineToOptions,
  type DeadlineOption,
} from '../../lib/lateRegistration.ts';
import { formatAmount } from './tournamentDisplay';
import {
  buildCatalogCommitPayload,
  catalogErrorMessage,
  normalizeSelection,
  validateCatalogResolutions,
} from '../venue-onboarding-ui';

async function fetchTemplates(): Promise<TournamentTemplateDto[]> {
  return api.get<TournamentTemplateDto[]>('/tournament/templates');
}

async function fetchBlindStructures(): Promise<BlindStructureDto[]> {
  return api.get<BlindStructureDto[]>('/tournament/blind-structures');
}

function downloadPackage(pkg: CatalogPackage) {
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(pkg, null, 2)], {
      type: 'application/json',
    }),
  );
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `pokerroom-catalog-v${pkg.version}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}

/** Panel errors: a missing permission is explained; anything else keeps the shared wording. */
// The package API sends one 403 per missing permission; only the payout-rule one has a UI hint.
const PAYOUT_FORBIDDEN = 'Payout rule editor role and update permission are required';

export function catalogPanelError(error: unknown, t: (key: string) => string): string {
  if (isAxiosError(error) && error.response?.status === 403) {
    const data: unknown = error.response.data;
    const message =
      data && typeof data === 'object' ? (data as { message?: unknown }).message : undefined;
    const messages = Array.isArray(message) ? message : [message];
    if (messages.includes(PAYOUT_FORBIDDEN)) return t('catalogForbidden');
  }
  return catalogErrorMessage(error);
}

function CatalogPayoutRuleDetails({ rule }: { rule: PayoutRule }) {
  const { t } = useTranslation('tournament');
  return (
    <div className="text-sm space-y-1 tabular-nums">
      <p>
        {t('catalogPrimaryPool')}: {rule.pool.primaryPercent}% · {t('catalogRankBonusPool')}:{' '}
        {rule.pool.rankBonusPercent}%
      </p>
      {rule.rankTiers.map((tier, index) => (
        <p key={index}>
          {tier.maxEntrants === null
            ? t('catalogEntrantsAny')
            : t('catalogEntrantsUpTo', { count: tier.maxEntrants })}
          : {tier.shares.map((share, rank) => `${rank + 1}: ${share}%`).join(' / ')}
        </p>
      ))}
      <p>
        {t('catalogRoundingUnit')}: {rule.roundingUnit}
      </p>
    </div>
  );
}

/** Shared by the templates and blind structures pages; it carries its own 44px field sizing. */
export function CatalogPackageWorkspace() {
  const { t } = useTranslation('tournament');
  const queryClient = useQueryClient();
  const [templateIds, setTemplateIds] = useState<string[]>([]);
  const [blindIds, setBlindIds] = useState<string[]>([]);
  const [levelIds, setLevelIds] = useState<string[]>([]);
  const [payoutTypes, setPayoutTypes] = useState<TournamentType[]>([]);
  const previewRequest = useRef(0);
  const [readingFile, setReadingFile] = useState(false);
  const { data: levels = [] } = useSettlementLevels();
  const { data: payoutDefaults } = useQuery({
    queryKey: ['venue-payout-rules'],
    queryFn: () =>
      api.get<{ rules: Array<{ type: TournamentType; version: number; rule: PayoutRule }> }>(
        '/tournament/payout-rules',
      ),
  });
  const [preview, setPreview] = useState<CatalogImportPreview | null>(null);
  const [resolutions, setResolutions] = useState<CatalogResolution[]>([]);
  const [message, setMessage] = useState<{ kind: 'error' | 'success'; text: string } | null>(null);
  const { data: templates = [] } = useQuery({ queryKey: ['templates'], queryFn: fetchTemplates });
  const { data: blinds = [] } = useQuery({
    queryKey: ['blind-structures'],
    queryFn: fetchBlindStructures,
  });

  const exportMutation = useMutation({
    mutationFn: () =>
      api.post<CatalogPackage>('/tournament/catalog/export', {
        templateIds: normalizeSelection(templateIds),
        blindStructureIds: normalizeSelection(blindIds),
        timedSettlementLevelIds: normalizeSelection(levelIds),
        payoutRuleTypes: payoutTypes,
      }),
    onSuccess: (pkg) => downloadPackage(pkg),
    onError: (error: Error) => setMessage({ kind: 'error', text: catalogPanelError(error, t) }),
  });
  const previewMutation = useMutation({
    mutationFn: ({ pkg }: { pkg: CatalogPackage; request: number }) =>
      api.post<CatalogImportPreview>('/tournament/catalog/import/preview', { package: pkg }),
    onSuccess: (result, variables) => {
      if (variables.request !== previewRequest.current) return;
      setPreview(result);
      setResolutions([]);
      setMessage(null);
    },
    onError: (error: Error, variables) => {
      if (variables.request === previewRequest.current) {
        setPreview(null);
        setMessage({ kind: 'error', text: catalogPanelError(error, t) });
      }
    },
  });
  const commitMutation = useMutation({
    mutationFn: () => {
      if (!preview) throw new Error(t('catalogPreviewRequired'));
      const error = validateCatalogResolutions(preview, resolutions);
      if (error) throw new Error(error);
      return api.post<CatalogImportResult>(
        '/tournament/catalog/import/commit',
        buildCatalogCommitPayload(preview, resolutions),
      );
    },
    onSuccess: (result) => {
      setMessage({
        kind: 'success',
        text: t('catalogCommitSuccess', {
          blinds: result.created.blindStructures.length,
          templates: result.created.templates.length,
          levels: result.created.timedSettlementLevels?.length ?? 0,
          payouts: result.created.payoutRules?.length ?? 0,
          replaced: result.replaced?.payoutRules.length ?? 0,
          reused:
            (result.reused?.timedSettlementLevels.length ?? 0) +
            (result.reused?.payoutRules.length ?? 0),
          omitted: result.omitted?.length ?? 0,
        }),
      });
      setPreview(null);
      setResolutions([]);
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
      void queryClient.invalidateQueries({ queryKey: ['blind-structures'] });
      void queryClient.invalidateQueries({ queryKey: ['timed-settlement-levels'] });
      void queryClient.invalidateQueries({ queryKey: ['venue-payout-rules'] });
    },
    onError: (error: Error) => setMessage({ kind: 'error', text: catalogPanelError(error, t) }),
  });

  const setResolution = (next: CatalogResolution) =>
    setResolutions((current) => [
      ...current.filter((item) => !(item.kind === next.kind && item.sourceKey === next.sourceKey)),
      next,
    ]);

  const groups = [
    {
      key: 'catalogSelectTemplates',
      items: templates.map((item) => ({ id: item.id, name: item.name })),
      selected: templateIds,
      toggle: (ids: string[]) => setTemplateIds(ids),
    },
    {
      key: 'catalogSelectBlinds',
      items: blinds.map((item) => ({ id: item.id, name: item.name })),
      selected: blindIds,
      toggle: (ids: string[]) => setBlindIds(ids),
    },
    {
      key: 'catalogSelectLevels',
      items: levels.map((item) => ({ id: item.id, name: item.name })),
      selected: levelIds,
      toggle: (ids: string[]) => setLevelIds(ids),
    },
    {
      key: 'catalogSelectPayouts',
      items: (payoutDefaults?.rules ?? []).map((item) => ({
        id: item.type,
        name: t(`type.${item.type}`),
      })),
      selected: payoutTypes as string[],
      toggle: (ids: string[]) => setPayoutTypes(ids as TournamentType[]),
    },
  ];

  return (
    <section className="relative mb-6 border border-base-300 bg-base-200 p-4 [&_button]:min-h-11 [&_button]:text-sm [&_input:not([type=checkbox])]:min-h-11 [&_input:not([type=checkbox])]:text-base [&_select]:min-h-11 [&_select]:text-base [&_label]:min-h-11 [&_label]:items-center">
      <details className="group">
        <summary className="min-h-11 flex items-center cursor-pointer font-bold text-base-content">
          {t('catalogPackageTitle')}
          <IconChevronDown
            size={20}
            stroke={1.5}
            aria-hidden="true"
            className="ml-auto transition-transform group-open:rotate-180"
          />
        </summary>
        <div className="space-y-6 mt-4">
          <p className="text-sm text-base-content/70">{t('catalogPackageDescription')}</p>

          <section aria-labelledby="catalog-export-title" className="space-y-4">
            <h3 id="catalog-export-title" className="text-base font-semibold text-base-content">
              {t('catalogExportHeading')}
            </h3>
            <div className="grid gap-4 md:grid-cols-2">
              {groups.map((group) => (
                <fieldset key={group.key} className="relative">
                  <legend className="text-sm font-semibold text-base-content mb-1">
                    {t(group.key)}
                    <span className="ml-2 font-normal text-base-content/70 tabular-nums">
                      {t('catalogSelectedCount', {
                        selected: group.selected.length,
                        total: group.items.length,
                      })}
                    </span>
                  </legend>
                  <ul>
                    {group.items.map((item) => (
                      <li key={item.id}>
                        <label className="flex gap-3 text-sm text-base-content cursor-pointer">
                          <input
                            type="checkbox"
                            className="h-5 w-5 shrink-0"
                            checked={group.selected.includes(item.id)}
                            onChange={(event) =>
                              group.toggle(
                                event.target.checked
                                  ? normalizeSelection([...group.selected, item.id])
                                  : group.selected.filter((id) => id !== item.id),
                              )
                            }
                          />
                          <span className="[overflow-wrap:anywhere]">{item.name}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </fieldset>
              ))}
            </div>
            <button
              type="button"
              className={OUTLINE}
              disabled={
                (!templateIds.length &&
                  !blindIds.length &&
                  !levelIds.length &&
                  !payoutTypes.length) ||
                exportMutation.isPending
              }
              onClick={() => exportMutation.mutate()}
            >
              {t('catalogExportSelected')}
            </button>
          </section>

          <section
            aria-labelledby="catalog-import-title"
            className="space-y-3 border-t border-base-300 pt-4"
          >
            <h3 id="catalog-import-title" className="text-base font-semibold text-base-content">
              {t('catalogImportHeading')}
            </h3>
            <p className="border border-warning p-2 text-sm text-base-content">
              {t('catalogPreviewNoWrites')}
            </p>
            <label
              className={`${OUTLINE} cursor-pointer focus-within:outline focus-within:outline-2 focus-within:outline-primary`}
            >
              {t('catalogUploadPreview')}
              <input
                type="file"
                accept=".json,application/json"
                className="sr-only"
                disabled={commitMutation.isPending}
                onChange={async (event) => {
                  const file = event.target.files?.[0];
                  if (!file) return;
                  const input = event.target;
                  const request = ++previewRequest.current;
                  setPreview(null);
                  setResolutions([]);
                  setMessage(null);
                  setReadingFile(true);
                  try {
                    const pkg = JSON.parse(await file.text()) as CatalogPackage;
                    if (request === previewRequest.current)
                      previewMutation.mutate({ pkg, request });
                  } catch (error) {
                    if (request === previewRequest.current)
                      setMessage({
                        kind: 'error',
                        text: error instanceof Error ? error.message : t('catalogInvalidJson'),
                      });
                  } finally {
                    if (request === previewRequest.current) setReadingFile(false);
                    input.value = '';
                  }
                }}
              />
            </label>
            {preview && (
              <CatalogImportReview
                preview={preview}
                resolutions={resolutions}
                onResolve={setResolution}
                commitDisabled={
                  commitMutation.isPending ||
                  previewMutation.isPending ||
                  readingFile ||
                  !!validateCatalogResolutions(preview, resolutions)
                }
                onCommit={() => commitMutation.mutate()}
              />
            )}
          </section>
          {message &&
            (message.kind === 'error' ? (
              <p role="alert" className="border border-error p-3 text-sm text-error">
                {message.text}
              </p>
            ) : (
              <p role="status" className="border border-success p-3 text-sm text-base-content">
                {message.text}
              </p>
            ))}
        </div>
      </details>
    </section>
  );
}

const CONFLICT_KIND_LABELS: Record<CatalogConflictKind, string> = {
  template: 'catalogSelectTemplates',
  blindStructure: 'catalogSelectBlinds',
  timedSettlementLevel: 'catalogSelectLevels',
  payoutRule: 'catalogSelectPayouts',
};

type CatalogConflictKind = CatalogImportPreview['conflicts'][number]['kind'];

/** Review of an uploaded package: counts, contents, conflicts, dependencies and commit. */
export function CatalogImportReview({
  preview,
  resolutions,
  onResolve,
  commitDisabled,
  onCommit,
}: {
  preview: CatalogImportPreview;
  resolutions: CatalogResolution[];
  onResolve: (next: CatalogResolution) => void;
  commitDisabled: boolean;
  onCommit: () => void;
}) {
  const { t } = useTranslation('tournament');
  const counts: Array<[string, number]> = [
    ['catalogCountBlinds', preview.proposedCreates.blindStructures.length],
    ['catalogCountTemplates', preview.proposedCreates.templates.length],
    ['catalogCountLevels', preview.proposedCreates.timedSettlementLevels?.length ?? 0],
    ['catalogCountPayouts', preview.conflicts.filter((item) => item.kind === 'payoutRule').length],
    [
      'catalogCountReused',
      (preview.reused?.timedSettlementLevels.length ?? 0) +
        (preview.reused?.payoutRules.length ?? 0),
    ],
  ];
  const contents = [
    {
      label: 'catalogSelectTemplates',
      names: preview.package.templates.map((item) => item.values.name),
    },
    {
      label: 'catalogSelectBlinds',
      names: preview.package.blindStructures.map((item) => item.values.name),
    },
    {
      label: 'catalogSelectLevels',
      names:
        preview.package.version === 2
          ? preview.package.timedSettlementLevels.map((item) => item.values.name)
          : [],
    },
    {
      label: 'catalogSelectPayouts',
      names:
        preview.package.version === 2
          ? preview.package.payoutRules.map((item) => t(`type.${item.type}`))
          : [],
    },
  ];
  const templateResolution = (sourceKey: string) =>
    resolutions.find((entry) => entry.kind === 'template' && entry.sourceKey === sourceKey);
  return (
    <div className="space-y-4 border-t border-base-300 pt-3 text-sm text-base-content">
      <table className="relative w-full max-w-md">
        <caption className="mb-2 text-left text-base font-semibold">
          {t('catalogPreviewHeading')}
        </caption>
        <tbody>
          {counts.map(([key, value]) => (
            <tr key={key} className="border-b border-base-300">
              <th scope="row" className="px-2 py-2 text-left font-normal">
                {t(key)}
              </th>
              <td className="px-2 py-2 text-right font-semibold tabular-nums">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="grid gap-3 md:grid-cols-2">
        {contents.map((group) => (
          <div key={group.label}>
            <p className="font-semibold">{t(group.label)}</p>
            <ul className="text-base-content/70">
              {group.names.map((name) => (
                <li key={name}>{name}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      {preview.errors.length > 0 && (
        <ul role="alert" className="border border-error p-3 text-error space-y-1">
          {preview.errors.map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      )}
      {preview.conflicts.map((conflict, index) => {
        const resolution = resolutions.find(
          (item) => item.kind === conflict.kind && item.sourceKey === conflict.sourceKey,
        );
        const id = `catalog-conflict-${index}`;
        const name =
          conflict.kind === 'payoutRule'
            ? t(`type.${conflict.incomingName}`)
            : conflict.incomingName;
        return (
          <div
            key={`${conflict.kind}:${conflict.sourceKey}`}
            className="space-y-2 border border-warning p-3"
          >
            <p className="font-medium">
              {name}（{t(CONFLICT_KIND_LABELS[conflict.kind])}）
            </p>
            <div>
              <label htmlFor={id} className="block text-sm mb-1">
                {t('catalogResolutionFor', { name })}
              </label>
              <select
                id={id}
                className={FIELD}
                value={resolution?.action ?? ''}
                onChange={(event) =>
                  onResolve({
                    kind: conflict.kind,
                    sourceKey: conflict.sourceKey,
                    action: event.target.value as CatalogResolution['action'],
                    ...(conflict.kind === 'payoutRule'
                      ? { expectedVersion: conflict.expectedVersion }
                      : {}),
                  })
                }
              >
                <option value="">{t('catalogChooseResolution')}</option>
                {conflict.choices.includes('rename') && (
                  <option value="rename">{t('catalogRename')}</option>
                )}
                {conflict.choices.includes('replace') && (
                  <option value="replace">{t('catalogReplace')}</option>
                )}
                {conflict.choices.includes('omit') && (
                  <option value="omit">{t('catalogOmit')}</option>
                )}
                <option value="cancel">{t('catalogCancel')}</option>
              </select>
            </div>
            {conflict.kind === 'payoutRule' && (
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="border border-base-300 p-2">
                  <p className="font-medium mb-1">{t('catalogCurrentRule')}</p>
                  {conflict.currentRule && <CatalogPayoutRuleDetails rule={conflict.currentRule} />}
                </div>
                <div className="border border-base-300 p-2">
                  <p className="font-medium mb-1">{t('catalogIncomingRule')}</p>
                  {conflict.incomingRule && (
                    <CatalogPayoutRuleDetails rule={conflict.incomingRule} />
                  )}
                </div>
              </div>
            )}
            {resolution?.action === 'replace' && (
              <div>
                <label htmlFor={`${id}-reason`} className="block text-sm mb-1">
                  {t('catalogReplacementReason')}
                </label>
                <input
                  id={`${id}-reason`}
                  className={FIELD}
                  value={resolution.reason ?? ''}
                  onChange={(event) => onResolve({ ...resolution, reason: event.target.value })}
                />
              </div>
            )}
            {resolution?.action === 'rename' && (
              <div>
                <label htmlFor={`${id}-name`} className="block text-sm mb-1">
                  {t('catalogNewName')}
                </label>
                <input
                  id={`${id}-name`}
                  className={FIELD}
                  value={resolution.name ?? ''}
                  onChange={(event) =>
                    onResolve({
                      ...resolution,
                      name: event.target.value,
                    })
                  }
                />
              </div>
            )}
          </div>
        );
      })}
      {preview.package.templates
        .filter((item) => item.blindStructureSourceKey)
        .map((item) => (
          <div key={item.sourceKey}>
            <label htmlFor={`catalog-blind-dep-${item.sourceKey}`} className="block text-sm mb-1">
              {item.values.name}: {t('catalogDependency')}
            </label>
            <select
              id={`catalog-blind-dep-${item.sourceKey}`}
              className={FIELD}
              value={
                templateResolution(item.sourceKey)?.blindStructureSourceKey ??
                item.blindStructureSourceKey ??
                ''
              }
              onChange={(event) =>
                onResolve({
                  kind: 'template',
                  sourceKey: item.sourceKey,
                  ...templateResolution(item.sourceKey),
                  blindStructureSourceKey: event.target.value || null,
                })
              }
            >
              <option value="">{t('catalogNoDependency')}</option>
              {preview.package.blindStructures.map((blind) => (
                <option key={blind.sourceKey} value={blind.sourceKey}>
                  {blind.values.name}
                </option>
              ))}
            </select>
          </div>
        ))}
      {preview.package.version === 2 &&
        preview.package.templates
          .filter((item) => item.timedSettlementLevelSourceKey)
          .map((item) => (
            <div key={`level:${item.sourceKey}`}>
              <label htmlFor={`catalog-level-dep-${item.sourceKey}`} className="block text-sm mb-1">
                {item.values.name}: {t('catalogLevelDependency')}
              </label>
              <select
                id={`catalog-level-dep-${item.sourceKey}`}
                className={FIELD}
                value={
                  templateResolution(item.sourceKey)?.timedSettlementLevelSourceKey ??
                  item.timedSettlementLevelSourceKey ??
                  ''
                }
                onChange={(event) =>
                  onResolve({
                    ...templateResolution(item.sourceKey),
                    kind: 'template',
                    sourceKey: item.sourceKey,
                    timedSettlementLevelSourceKey: event.target.value,
                  })
                }
              >
                {preview.package.version === 2 &&
                  preview.package.timedSettlementLevels.map((level) => (
                    <option
                      key={level.sourceKey}
                      value={level.sourceKey}
                      disabled={
                        level.values.startingChips !== item.values.startingChips ||
                        resolutions.some(
                          (entry) =>
                            entry.kind === 'timedSettlementLevel' &&
                            entry.sourceKey === level.sourceKey &&
                            entry.action === 'omit',
                        )
                      }
                    >
                      {level.values.name}
                    </option>
                  ))}
              </select>
            </div>
          ))}
      <button type="button" className={PRIMARY} disabled={commitDisabled} onClick={onCommit}>
        {t('catalogCommitReviewed')}
      </button>
    </div>
  );
}

const ACTION =
  'relative inline-flex min-h-11 items-center justify-center gap-1 px-4 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50';
const PRIMARY = `${ACTION} bg-primary text-primary-content hover:bg-primary/90`;
const OUTLINE = `${ACTION} border border-base-300 text-base-content hover:bg-base-300`;
const DANGER_OUTLINE = `${ACTION} border border-error text-error hover:bg-error/10`;
const FIELD = 'h-11 w-full border border-base-300 bg-base-100 px-3 text-base text-base-content';
const LABEL = 'block text-sm text-base-content mb-1';
const CHECK_LABEL = 'flex min-h-11 items-center gap-3 font-medium text-base-content';
const BADGE = 'inline-flex h-6 items-center px-2 text-xs font-medium';

export default function TournamentTemplatesPage() {
  const { t, i18n } = useTranslation('tournament');
  const locale = i18n.language;
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [feeDraft, setFeeDraft] = useState<TimedFeeTable>();
  const [feeEdited, setFeeEdited] = useState(false);

  const {
    data: templates = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['templates'],
    queryFn: fetchTemplates,
  });
  const { data: settlementLevels = [] } = useSettlementLevels();

  const { data: blindStructures = [] } = useQuery({
    queryKey: ['blind-structures'],
    queryFn: fetchBlindStructures,
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateTemplateDto>({
    defaultValues: {
      type: 'ELIMINATION',
      payoutType: 'STANDARD',
      startingChips: 10000,
      lateRegLevels: 4,
      buyinAmount: '1000',
      blindStructureId: '',
      rebuyAllowed: false,
      maxRebuy: 0,
      addonAllowed: false,
      maxAddon: 0,
      manualCountMode: false,
      gtdPrizePool: '0',
      bountyAmount: '0',
      rakePct: '10',
    },
  });

  const timed = watch('type') === 'TIMED';
  const principal = watch('buyinAmount') ?? '';
  const rebuyAllowed = watch('rebuyAllowed');
  const addonAllowed = watch('addonAllowed');
  const selectedBlindStructureId = watch('blindStructureId');
  const lateRegLevels = watch('lateRegLevels');
  const rebuyDeadline = watch('rebuyDeadline');
  const addonDeadline = watch('addonDeadline');
  const deadlineOptions = buildDeadlineOptions(
    blindStructures.find((structure) => structure.id === selectedBlindStructureId),
  );
  useEffect(() => {
    const maximum = deadlineOptions.length;
    const nextLateReg = clampDeadlineToOptions(lateRegLevels, maximum);
    const nextRebuy = clampDeadlineToOptions(rebuyDeadline, maximum);
    const nextAddon = clampDeadlineToOptions(addonDeadline, maximum);
    if (nextLateReg !== lateRegLevels) setValue('lateRegLevels', nextLateReg);
    if (nextRebuy !== rebuyDeadline) setValue('rebuyDeadline', nextRebuy);
    if (nextAddon !== addonDeadline) setValue('addonDeadline', nextAddon);
  }, [addonDeadline, deadlineOptions.length, lateRegLevels, rebuyDeadline, setValue]);

  const createMutation = useMutation({
    mutationFn: (dto: CreateTemplateDto) => api.post('/tournament/templates', dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
      resetForm();
    },
    onError: (err: Error) => setError(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: Partial<CreateTemplateDto> }) =>
      api.patch(`/tournament/templates/${id}`, dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
      resetForm();
    },
    onError: (err: Error) => setError(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/tournament/templates/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['templates'] }),
    onError: (err: Error) => setNotice(err.message),
  });

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setError('');
    setFeeDraft(undefined);
    setFeeEdited(false);
    reset({
      type: 'ELIMINATION',
      payoutType: 'STANDARD',
      startingChips: 10000,
      lateRegLevels: 4,
      buyinAmount: '1000',
      blindStructureId: '',
      rebuyAllowed: false,
      maxRebuy: 0,
      addonAllowed: false,
      maxAddon: 0,
      manualCountMode: false,
      gtdPrizePool: '0',
      bountyAmount: '0',
      rakePct: '10',
    });
  };

  const startEdit = (tmpl: TournamentTemplateDto) => {
    setFeeDraft(tmpl.timedFeeVersion?.table);
    setFeeEdited(false);
    setEditingId(tmpl.id);
    setShowForm(true);
    setError('');
    setValue('name', tmpl.name);
    setValue('description', tmpl.description ?? '');
    setValue('type', tmpl.type);
    setValue('payoutType', tmpl.payoutType);
    setValue('startingChips', tmpl.startingChips);
    setValue('lateRegLevels', tmpl.lateRegLevels);
    setValue('buyinAmount', tmpl.buyinAmount ?? '0');
    setValue('blindStructureId', tmpl.blindStructureId ?? '');
    setValue('rebuyAllowed', tmpl.rebuyAllowed);
    setValue('rebuyDeadline', tmpl.rebuyDeadline);
    setValue('rebuyCost', tmpl.rebuyCost);
    setValue('rebuyChips', tmpl.rebuyChips);
    setValue('maxRebuy', tmpl.maxRebuy);
    setValue('addonAllowed', tmpl.addonAllowed);
    setValue('addonDeadline', tmpl.addonDeadline);
    setValue('addonCost', tmpl.addonCost);
    setValue('addonChips', tmpl.addonChips);
    setValue('maxAddon', tmpl.maxAddon);
    setValue('manualCountMode', tmpl.manualCountMode);
    setValue('timedSettlementLevelId', tmpl.timedSettlementLevelId ?? undefined);
    setValue('gtdPrizePool', tmpl.gtdPrizePool);
    setValue('bountyAmount', tmpl.bountyAmount);
    setValue('rakePct', tmpl.rakePct);
    (setValue as (k: string, v: unknown) => void)(
      'displaySideInfo',
      Array.isArray(tmpl.displaySideInfo) ? (tmpl.displaySideInfo as string[]).join('\n') : '',
    );
    (setValue as (k: string, v: unknown) => void)('displayMarquee', tmpl.displayMarquee ?? '');
  };

  const onSubmit = (data: CreateTemplateDto) => {
    // Convert textarea string (from react-hook-form) to array
    if (timed && !editingId && !feeDraft) {
      setError(t('timedFees.required'));
      return;
    }
    const raw = data as unknown as Record<string, unknown>;
    const sideInfoRaw = (raw.displaySideInfo as string) ?? '';
    const dto: CreateTemplateDto = {
      ...data,
      ...(timed &&
      feeDraft &&
      (!editingId || feeEdited || !sameMoneyAmount(feeDraft.principalAmount, principal))
        ? {
            timedFeeTable: { ...feeDraft, principalAmount: principal },
            rebuyCost: principal,
            addonCost: principal,
          }
        : {}),
      blindStructureId: data.blindStructureId || undefined,
      timedSettlementLevelId: data.timedSettlementLevelId || (editingId ? null : undefined),
      displaySideInfo:
        typeof sideInfoRaw === 'string'
          ? sideInfoRaw.split('\n').filter((l: string) => l.trim() !== '')
          : (sideInfoRaw as string[]),
      displayMarquee: (raw.displayMarquee as string) ?? '',
    };
    if (editingId) {
      updateMutation.mutate({ id: editingId, dto });
    } else {
      createMutation.mutate(dto);
    }
  };

  const handleDelete = (tmpl: TournamentTemplateDto) => {
    if (tmpl.isDefault) {
      alert(t('cannotDeleteDefault'));
      return;
    }
    if (!confirm(t('confirmDisableTemplate', { name: tmpl.name }))) return;
    deleteMutation.mutate(tmpl.id);
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-base-content">{t('templateManagement')}</h1>
        {!showForm && (
          <button
            type="button"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            className={PRIMARY}
          >
            {t('addTemplate')}
          </button>
        )}
      </div>

      {notice && (
        <p role="alert" className="mb-4 border border-error p-3 text-sm text-error">
          {notice}
        </p>
      )}

      {/* Form */}
      {showForm && (
        <form
          onSubmit={handleSubmit(onSubmit)}
          aria-labelledby="template-form-title"
          className="bg-base-200 border border-base-300 p-4 sm:p-6 mb-6 space-y-4"
        >
          <h2 id="template-form-title" className="text-base-content font-medium text-lg">
            {editingId ? t('editTemplate') : t('addTemplate')}
          </h2>

          {timed && (
            <TimedFeeEditor
              value={feeDraft}
              principal={principal}
              legacyRake={watch('rakePct') ?? '0'}
              onChange={(value) => {
                setFeeDraft(value);
                setFeeEdited(true);
              }}
            />
          )}
          <TemplateFormFields
            register={register}
            errors={errors}
            settlementLevels={settlementLevels}
            blindStructures={blindStructures}
            deadlineOptions={deadlineOptions}
            rebuyAllowed={!!rebuyAllowed}
            addonAllowed={!!addonAllowed}
            timed={timed}
            fixedPrincipal={timed && feeDraft ? principal : undefined}
            onTimedLevelChange={(id) => {
              const level = settlementLevels.find((item) => item.id === id);
              setValue('timedSettlementLevelId', level?.id);
              if (level) {
                setValue('startingChips', level.startingChips);
                setValue('type', 'TIMED');
                setValue('manualCountMode', true);
              }
            }}
          />

          {error && (
            <p role="alert" className="border border-error p-3 text-error text-sm">
              {error}
            </p>
          )}

          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={resetForm} className={OUTLINE}>
              {t('cancel', { ns: 'common' })}
            </button>
            <button type="submit" disabled={isPending} className={PRIMARY}>
              {isPending ? t('saving') : editingId ? t('updateTemplate') : t('createTemplate')}
            </button>
          </div>
        </form>
      )}

      {/* Template List */}
      {isError ? (
        <div role="alert" className="py-12 text-center space-y-3">
          <p className="text-base text-base-content">{t('templatesLoadError')}</p>
          <button type="button" onClick={() => void refetch()} className={OUTLINE}>
            {t('retry')}
          </button>
        </div>
      ) : isLoading ? (
        <p role="status" className="text-base-content/70 text-center py-12">
          {t('loading')}
        </p>
      ) : templates.length === 0 ? (
        <p className="text-base-content text-center py-12">{t('noTemplates')}</p>
      ) : (
        <ul className="space-y-3">
          {templates.map((tmpl) => {
            const facts: Array<[string, string]> = [];
            if (parseFloat(tmpl.buyinAmount) > 0)
              facts.push(['buyin', formatAmount(tmpl.buyinAmount, locale)]);
            facts.push(['startingChips', tmpl.startingChips.toLocaleString(locale)]);
            if (tmpl.blindStructure) facts.push(['blind', tmpl.blindStructure.name]);
            if (tmpl.type === 'TIMED' && tmpl.timedFeeVersion) {
              facts.push(['originalVenueFee', tmpl.timedFeeVersion.table.originalVenueFeeAmount]);
            } else {
              facts.push(['rake', `${tmpl.rakePct}%`]);
            }
            if (tmpl.timedSettlementLevelId)
              facts.push([
                'timedLevel',
                settlementLevels.find((level) => level.id === tmpl.timedSettlementLevelId)?.name ??
                  t('timedLevels.disabledLevel'),
              ]);
            facts.push(['lateReg', t('templateFacts.levelValue', { level: tmpl.lateRegLevels })]);
            if (tmpl.rebuyAllowed)
              facts.push(['rebuy', t('templateFacts.untilLevel', { level: tmpl.rebuyDeadline })]);
            if (tmpl.addonAllowed)
              facts.push(['addon', t('templateFacts.untilLevel', { level: tmpl.addonDeadline })]);
            if (parseFloat(tmpl.gtdPrizePool) > 0)
              facts.push(['gtd', formatAmount(tmpl.gtdPrizePool, locale)]);
            if (parseFloat(tmpl.bountyAmount) > 0)
              facts.push(['bounty', formatAmount(tmpl.bountyAmount, locale)]);
            return (
              <li
                key={tmpl.id}
                className="bg-base-200 border border-base-300 p-4 flex flex-col sm:flex-row items-start justify-between gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h2 className="text-base-content font-bold text-lg [overflow-wrap:anywhere]">
                      {tmpl.name}
                    </h2>
                    {tmpl.isDefault && (
                      <span className={`${BADGE} bg-primary/10 text-primary`}>
                        {t('defaultLabel')}
                      </span>
                    )}
                    <span className={`${BADGE} border border-base-300 text-base-content`}>
                      {t(`type.${tmpl.type}`)}
                    </span>
                    <span className={`${BADGE} border border-base-300 text-base-content`}>
                      {t(`payoutType.${tmpl.payoutType}`)}
                    </span>
                  </div>
                  {tmpl.description && (
                    <p className="text-base-content/70 text-sm mb-2">{tmpl.description}</p>
                  )}
                  <dl className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                    {facts.map(([key, value]) => (
                      <div key={key} className="min-w-0">
                        <dt className="text-xs text-base-content/70">
                          {t(
                            key === 'originalVenueFee'
                              ? 'timedFees.original'
                              : `templateFacts.${key}`,
                          )}
                        </dt>
                        <dd className="font-medium text-base-content tabular-nums [overflow-wrap:anywhere]">
                          {value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
                <div className="flex gap-2 sm:ml-4 shrink-0">
                  <button type="button" onClick={() => startEdit(tmpl)} className={OUTLINE}>
                    {t('edit', { ns: 'common' })}
                    <span className="sr-only">{tmpl.name}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(tmpl)}
                    disabled={tmpl.isDefault || deleteMutation.isPending}
                    className={DANGER_OUTLINE}
                  >
                    {t('disable')}
                    <span className="sr-only">{tmpl.name}</span>
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <CatalogPackageWorkspace />
    </div>
  );
}

/** Template form fields; the parent owns the form state and submission. */
export function TemplateFormFields({
  register,
  errors,
  settlementLevels,
  blindStructures,
  deadlineOptions,
  rebuyAllowed,
  addonAllowed,
  onTimedLevelChange,
  timed = false,
  fixedPrincipal,
}: {
  register: UseFormRegister<CreateTemplateDto>;
  errors: FieldErrors<CreateTemplateDto>;
  settlementLevels: Array<{ id: string; name: string }>;
  blindStructures: Array<{ id: string; name: string }>;
  deadlineOptions: DeadlineOption[];
  rebuyAllowed: boolean;
  addonAllowed: boolean;
  onTimedLevelChange: (id: string) => void;
  timed?: boolean;
  fixedPrincipal?: string;
}) {
  const { t } = useTranslation('tournament');
  const registerText = register as (key: string) => Record<string, unknown>;
  const deadlineSelect = (id: string, field: 'rebuyDeadline' | 'addonDeadline') => (
    <div>
      <label htmlFor={id} className={LABEL}>
        {t('deadlineLevel')}
      </label>
      <select
        id={id}
        {...register(field, { valueAsNumber: true })}
        disabled={deadlineOptions.length === 0}
        className={FIELD}
      >
        {deadlineOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
  const purchaseFields = (
    prefix: 'rebuy' | 'addon',
    fields: {
      deadline: 'rebuyDeadline' | 'addonDeadline';
      cost: 'rebuyCost' | 'addonCost';
      chips: 'rebuyChips' | 'addonChips';
      max: 'maxRebuy' | 'maxAddon';
    },
  ) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
      {deadlineSelect(`tmpl-${prefix}-deadline`, fields.deadline)}
      <div>
        <label htmlFor={`tmpl-${prefix}-cost`} className={LABEL}>
          {t('cost')}
        </label>
        <input
          id={`tmpl-${prefix}-cost`}
          {...register(fields.cost)}
          readOnly={fixedPrincipal !== undefined}
          value={fixedPrincipal}
          type="text"
          className={`${FIELD} text-right tabular-nums`}
        />
      </div>
      <div>
        <label htmlFor={`tmpl-${prefix}-chips`} className={LABEL}>
          {t('chipsAdded')}
        </label>
        <input
          id={`tmpl-${prefix}-chips`}
          {...register(fields.chips, { valueAsNumber: true })}
          type="number"
          className={`${FIELD} text-right tabular-nums`}
        />
      </div>
      <div>
        <label htmlFor={`tmpl-${prefix}-max`} className={LABEL}>
          {t('maxCount')}
        </label>
        <input
          id={`tmpl-${prefix}-max`}
          {...register(fields.max, { valueAsNumber: true })}
          type="number"
          min={0}
          className={`${FIELD} text-right tabular-nums`}
        />
      </div>
    </div>
  );
  return (
    <>
      {/* Name + Description */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="tmpl-name" className={LABEL}>
            {t('templateName')} *
          </label>
          <input
            id="tmpl-name"
            {...register('name', { required: t('nameRequiredTemplate') })}
            aria-invalid={errors.name ? true : undefined}
            aria-describedby={errors.name ? 'tmpl-name-error' : undefined}
            className={FIELD}
            placeholder={t('templateNamePlaceholder')}
          />
          {errors.name && (
            <p id="tmpl-name-error" className="text-error text-sm mt-1">
              {errors.name.message}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="tmpl-description" className={LABEL}>
            {t('description')}
          </label>
          <input
            id="tmpl-description"
            {...register('description')}
            className={FIELD}
            placeholder={t('optional', { ns: 'common' })}
          />
        </div>
      </div>

      {/* Type + Payout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="tmpl-type" className={LABEL}>
            {t('tournamentType')}
          </label>
          <select id="tmpl-type" {...register('type')} className={FIELD}>
            <option value="ELIMINATION">{t('type.ELIMINATION')}</option>
            <option value="TIMED">{t('type.TIMED')}</option>
            <option value="BOUNTY">{t('type.BOUNTY')}</option>
            <option value="SPECIAL">{t('type.SPECIAL')}</option>
          </select>
        </div>
        <div>
          <label htmlFor="tmpl-payout-type" className={LABEL}>
            {t('payoutMode')}
          </label>
          <select id="tmpl-payout-type" {...register('payoutType')} className={FIELD}>
            <option value="STANDARD">{t('payoutType.STANDARD')}</option>
            <option value="ICM">{t('payoutType.ICM')}</option>
            <option value="GTD">{t('payoutType.GTD')}</option>
            <option value="BOUNTY">{t('payoutType.BOUNTY')}</option>
          </select>
        </div>
      </div>

      {/* Timed settlement level */}
      <div>
        <label htmlFor="tmpl-timed-level" className={LABEL}>
          {t('timedLevels.templateLevel')}
        </label>
        <select
          id="tmpl-timed-level"
          {...register('timedSettlementLevelId')}
          className={FIELD}
          onChange={(event) => onTimedLevelChange(event.target.value)}
        >
          <option value="">{t('timedLevels.unboundLevel')}</option>
          {settlementLevels.map((level) => (
            <option key={level.id} value={level.id}>
              {level.name}
            </option>
          ))}
        </select>
      </div>

      {/* Buyin + Chips */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="tmpl-buyin" className={LABEL}>
            {t('buyinAmountLabel')} *
          </label>
          <input
            id="tmpl-buyin"
            {...register('buyinAmount')}
            type="text"
            inputMode="decimal"
            className={`${FIELD} text-right tabular-nums`}
            placeholder="1000"
          />
        </div>
        <div>
          <label htmlFor="tmpl-starting-chips" className={LABEL}>
            {t('startingChipsLabel')} *
          </label>
          <input
            id="tmpl-starting-chips"
            {...register('startingChips', { required: true, valueAsNumber: true })}
            type="number"
            className={`${FIELD} text-right tabular-nums`}
          />
        </div>
      </div>

      {/* Blind Structure */}
      <div>
        <label htmlFor="tmpl-blind-structure" className={LABEL}>
          {t('blindStructure')}
        </label>
        <select id="tmpl-blind-structure" {...register('blindStructureId')} className={FIELD}>
          <option value="">{t('noSpecify')}</option>
          {blindStructures.map((bs) => (
            <option key={bs.id} value={bs.id}>
              {bs.name}
            </option>
          ))}
        </select>
      </div>

      {/* Rake + Late Reg */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {!timed && (
          <div>
            <label htmlFor="tmpl-rake" className={LABEL}>
              {t('rakePercent')}
            </label>
            <input
              id="tmpl-rake"
              {...register('rakePct')}
              type="text"
              inputMode="decimal"
              className={`${FIELD} text-right tabular-nums`}
            />
          </div>
        )}
        <div>
          <label htmlFor="tmpl-late-reg" className={LABEL}>
            {t('lateRegDeadline')}
          </label>
          <select
            id="tmpl-late-reg"
            {...register('lateRegLevels', { valueAsNumber: true })}
            disabled={deadlineOptions.length === 0}
            className={FIELD}
          >
            <option value={0}>{t('lateRegNone')}</option>
            {deadlineOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* GTD + Bounty */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="tmpl-gtd" className={LABEL}>
            {t('gtdPrizePoolLabel')}
          </label>
          <input
            id="tmpl-gtd"
            {...register('gtdPrizePool')}
            type="text"
            inputMode="decimal"
            className={`${FIELD} text-right tabular-nums`}
            placeholder="0"
          />
        </div>
        <div>
          <label htmlFor="tmpl-bounty" className={LABEL}>
            {t('bountyAmountLabel')}
          </label>
          <input
            id="tmpl-bounty"
            {...register('bountyAmount')}
            type="text"
            inputMode="decimal"
            className={`${FIELD} text-right tabular-nums`}
            placeholder="0"
          />
        </div>
      </div>

      {/* Rebuy */}
      <fieldset className="relative border border-base-300 p-4 space-y-3">
        <legend className="sr-only">{t('allowRebuy')}</legend>
        <label htmlFor="tmpl-rebuy" className={CHECK_LABEL}>
          <input
            {...register('rebuyAllowed')}
            type="checkbox"
            id="tmpl-rebuy"
            className="h-5 w-5"
          />
          {t('allowRebuy')}
        </label>
        {rebuyAllowed &&
          purchaseFields('rebuy', {
            deadline: 'rebuyDeadline',
            cost: 'rebuyCost',
            chips: 'rebuyChips',
            max: 'maxRebuy',
          })}
      </fieldset>

      {/* Addon */}
      <fieldset className="relative border border-base-300 p-4 space-y-3">
        <legend className="sr-only">{t('allowAddon')}</legend>
        <label htmlFor="tmpl-addon" className={CHECK_LABEL}>
          <input
            {...register('addonAllowed')}
            type="checkbox"
            id="tmpl-addon"
            className="h-5 w-5"
          />
          {t('allowAddon')}
        </label>
        {addonAllowed &&
          purchaseFields('addon', {
            deadline: 'addonDeadline',
            cost: 'addonCost',
            chips: 'addonChips',
            max: 'maxAddon',
          })}
      </fieldset>

      <div className="border border-base-300 p-4">
        <label htmlFor="tmpl-manual-count" className={CHECK_LABEL}>
          <input
            {...register('manualCountMode')}
            type="checkbox"
            id="tmpl-manual-count"
            aria-describedby="tmpl-manual-count-hint"
            className="h-5 w-5"
          />
          {t('manualCountMode')}
        </label>
        <p id="tmpl-manual-count-hint" className="text-sm text-base-content/70 mt-1">
          {t('manualCountModeHint')}
        </p>
      </div>

      {/* Display Settings */}
      <fieldset className="border border-base-300 p-4 space-y-3">
        <legend className="text-base-content font-medium flex items-center gap-2 px-1">
          <BrandIcon value="tv" size={24} />
          {t('displaySettings')}
        </legend>
        <div>
          <label htmlFor="tmpl-side-info" className={LABEL}>
            {t('sideInfoLabel')}
          </label>
          <textarea
            id="tmpl-side-info"
            {...registerText('displaySideInfo')}
            aria-describedby="tmpl-side-info-hint"
            className="w-full border border-base-300 bg-base-100 px-3 py-2 text-base text-base-content resize-y"
            rows={3}
            placeholder={t('sideInfoPlaceholder')}
          />
          <p id="tmpl-side-info-hint" className="text-base-content/70 text-sm mt-1">
            {t('sideInfoHint')}
          </p>
        </div>
        <div>
          <label htmlFor="tmpl-marquee" className={LABEL}>
            {t('marqueeLabel')}
          </label>
          <input
            id="tmpl-marquee"
            {...registerText('displayMarquee')}
            className={FIELD}
            placeholder={`${brand.name}`}
          />
        </div>
      </fieldset>
    </>
  );
}
